import React, { useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';

import { Card, CardMedia, CardActionArea } from '@prisjakt/ui/card';
import Clickable from '@prisjakt/ui-clickable';
import {
  BodySmallText,
  CaptionText,
  Behavior,
  FontEmphasis,
  FontColor,
} from '@prisjakt/ui/text/variants';
import InfoList from '@prisjakt/ui-info-list';

import { Badge } from '../badge';
import { ExpertUserCard } from '../expert-user-card';
import { trackCategoryPageExpertCarouselCard } from '../../../../components/advized/tracking';

import * as S from './styles';

import { ContentTypes } from '../../types';

export enum TrackingSource {
  ADVIZED_WIDGET = 'advizedWidget',
}

type Props = {
  type: ContentTypes;
  media?: React.ReactNode;
  category?: {
    name: string;
    url: string;
  };
  title: string;
  url: string;
  experts?: Array<{ name: string; avatar: string; url?: string }> | null;
  likes?: number | null;
  date?: string | null;
  grade?: number | null;
  style?: {};
  className?: string;
  trackingSource?: TrackingSource;
};

function ExpertContentCard({
  className,
  type,
  media,
  category,
  title,
  url,
  experts,
  likes,
  date,
  grade,
  style,
  trackingSource,
}: Props) {
  const { renderMode, linkTo, t, market } = useContext(AppContext);

  const getInfoListItems = () => {
    const render = (string: string) => {
      return (
        <CaptionText emphasis={FontEmphasis.HIGH} color={FontColor.DARK}>
          {string}
        </CaptionText>
      );
    };
    const likesString =
      likes && likes >= 50
        ? t('expert', 'likes.count', {
            n: likes, // Only used for pluralisation
            count: likes.toLocaleString(market.locale),
          })
        : undefined;

    return [
      ...(date ? [render(date)] : []),
      ...(likesString ? [render(likesString)] : []),
    ];
  };

  return (
    <Card className={className} style={{ maxWidth: '100%', ...style }}>
      <CardActionArea
        container={Clickable as any}
        containerProps={{
          onClick: () => {
            switch (trackingSource) {
              case TrackingSource.ADVIZED_WIDGET: {
                trackCategoryPageExpertCarouselCard(category?.url, type);
                break;
              }
              default:
            }
          },
          to: linkTo(
            'expertContent',
            { semantic_id: url },
            renderMode === 'native' ? { view: 'native' } : undefined
          ),
          isExternal: renderMode === 'native',
        }}
        style={{ flex: '1 0 auto' }}
      >
        {media && (
          <CardMedia>
            {grade && <S.Rating grade={grade} />}
            {media}
          </CardMedia>
        )}

        <S.Content>
          <S.Header>
            <BodySmallText
              behavior={Behavior.OnelineEllipsis}
              emphasis={FontEmphasis.MEDIUM}
            >
              {category && category.url && <>{category.name}</>}
            </BodySmallText>
          </S.Header>

          <S.Title>
            <Badge type={type} />: {title}
          </S.Title>

          <InfoList list={getInfoListItems()} />
        </S.Content>
      </CardActionArea>

      {experts && (
        <S.Footer>
          <ExpertUserCard experts={experts} />
        </S.Footer>
      )}
    </Card>
  );
}

export default ExpertContentCard;
